/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

// Components
import Wizard from 'shared/ui/components/Wizard.vue';
import EventType from './create-workspace/EventType.vue';
import Templates from './create-workspace/Templates.vue';
import WorkspaceForm from './create-workspace/WorkspaceForm.vue';
import WorkspaceSummary from './create-workspace/WorkspaceSummary.vue';
import EventProgress from 'ui/components/modals/EventProgress.vue';
import LocationTimezone from './create-workspace/LocationTimezone.vue';

// Utils
import { get, isEmpty } from 'lodash';

/**
 * This component is used to create a new workspace.
 *
 * It uses a `Wizard` to guide the user through the creation process.
 *
 *
 * @category ui.components.wizards
 * @exports CreateWorkspace
 * @component
 */
const __vue_script__ = {
    name: 'CreateWorkspace',

    components: { Wizard, Templates, WorkspaceForm, WorkspaceSummary, EventProgress },

    data() {
        return {
            error: null,
            form: {},
            jobProgressUrl: null,
            jobProgressPath: this.$const.CREATE_WORKSPACE_QUEUE_JOB_RESULT_PATH,
            progress: 0,
            trackingId: null,
            wizardCompleted: false,
            steps: [
                {
                    title: 'wizards.create_workspace.step_1_title',
                    component: Templates
                },
                {
                    title: 'wizards.create_workspace.step_2_title',
                    component: WorkspaceForm,
                    modalContentStyle: 'narrow'
                },
                {
                    title: 'wizards.create_workspace.event_type',
                    component: EventType,
                    modalContentStyle: 'narrow'
                },
                {
                    title: 'wizards.create_workspace.location_and_timezone',
                    component: LocationTimezone,
                    modalContentStyle: 'narrow'
                },
                {
                    title: 'wizards.create_workspace.step_3_title',
                    component: WorkspaceSummary,
                    nextLabel: 'wizards.create_workspace.action'
                }
            ]
        };
    },

    computed: {
        /**
         * @returns {Boolean} whether the wizard is complete or not
         */
        showWizard() {
            return !this.wizardCompleted;
        }
    },

    methods: {
        /**
         * Submits data to create the workspace with gathered information.
         *
         * @param {Object} data the wizard collected data
         */
        async submitForm(data) {
            await this.startTracking(data); // Yes, we try to track even without data

            if (isEmpty(data)) {
                console.warn("[CreateWorkspace] Can\'t create a workspace with no data!");
                this.setError('wizard.errors.no_data');
                return;
            }

            console.info("[CreateWorkspace] Wizard's collected data:", data);
            this.wizardCompleted = true;

            this.$utils.modals.toMediumModal(this.$el);

            const { jobId, node, error } = await this.registerJob(data);

            if (error || !jobId || !node) {
                // Something went wrong, stop here.
                console.error("[CreateWorkspace] Can't register the job.", error);
                this.setError(error || 'wizards.create_workspace.errors.job_registration');
                return;
            }

            this.jobProgressUrl = this.$utils.url.getBackstageURL(node, this.jobProgressPath);

            try {
                const minutesToTimeout = 15, interval = 1500;
                const { eid } = await this.$services.jobs.waitFor({
                    jobId,
                    statusPath: this.jobProgressUrl,
                    resultPath: this.jobProgressUrl,
                    updateUIProgress: this.updateProgress,
                    interval, minutesToTimeout,
                    node
                });
                const eventUrl = this.$services.event.getEventUrl({
                    _id: eid,
                    node
                });

                // Everything gone right, redirect the user to the newly created event.
                await this.stopTracking(eid);

                this.reset();
                this.$router.redirect(eventUrl);

            } catch (error) {
                console.error('[CreateWorkspace] Could not create workspace', error);
                this.$services.crashReporting.captureException(error);
                this.setError((error || {}).error || 'wizards.create_workspace.errors.job_creation_failed');
            }
        },

        /**
         * Stores the create workspace job on the backend job runner, if something goes wrong
         * during this process shows an error and exits.
         *
         * @param {Object} data the payload needed to create the workspace
         *
         * @returns {Promise<Object>} the job registration response
         */
        async registerJob(data) {
            const user = this.$services.user.getCurrentUser();
            const response = await this.$services.event.createEvent(data, user);
            const { error, message } = response;

            if (!error) {
                return response;
            }

            const msg = get(message, 'error') || error;

            return { error: msg };
        },

        /**
         * Updates the creation process progress.
         *
         * @param {Number} progress the current progress
         */
        updateProgress(progress) {
            this.progress = progress;
        },

        /**
         * Sets the error and displays it to the user.
         *
         * @param {String} error the eventual occurred error.
         */
        async setError(error) {
            this.error = error;

            await this.stopTracking(null, this.error);
        },

        /**
         * Starts the workspace creation process tracking
         *
         * @param {Object} data the wizard collected data
         */
        async startTracking(data) {
            const payload = {
                node: 'unknown',
                template: 'unknown'
            };

            if (!isEmpty(data)) {
                const rootNode = await this.$services.server.getRootForGroup(data.servers_location.value);

                payload.node = rootNode ? rootNode : 'unknown';
                payload.template = data.template.id;
            }

            this.trackingId = this.$services.track.startTracking('workspace_creation', payload);
        },

        /**
         * Interrupts the workspace creation process tracking
         *
         * @param {String} eid the just created event ID
         * @param {String} [error] an optional error message
         */
        async stopTracking(eid, error) {
            const payload = {
                eid: eid,
                ok: true
            };

            if (error) {
                payload.ok = false;
                payload.error = error;
            }

            await this.$services.track.endTracking(this.trackingId, payload);
        },

        /**
         * Resets the component to its initial state and closes the modal.
         */
        reset() {
            this.error = null;
            this.form = {};
            this.jobProgressUrl = null;
            this.jobProgressPath = this.$const.CREATE_WORKSPACE_QUEUE_JOB_RESULT_PATH;
            this.progress = 0;
            this.trackingId = null;
            this.wizardCompleted = false;

            this.$emit('close');
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"create-workspace"},[(_vm.showWizard)?_c('wizard',{attrs:{"initial-data":_vm.form,"steps":_vm.steps,"target":"_blank","keep-alive-exclude":"WorkspaceSummary"},on:{"wizard:completed":_vm.submitForm}},[_c('span',{staticClass:"close",on:{"click":function($event){return _vm.reset()}}}),_c('h1',[_vm._v(_vm._s(_vm.$t('wizards.create_workspace.main_title')))])]):_vm._e(),(!_vm.showWizard && !_vm.error)?_c('event-progress',{attrs:{"progress":_vm.progress,"title":_vm.$t('wizards.create_workspace.creation_title')}}):_vm._e(),(_vm.error)?_c('div',{staticClass:"errors modal-wrapper"},[_c('div',{staticClass:"modal-header"},[_c('h3',[_vm._v(_vm._s(_vm.$t('general.something_wrong')))])]),_c('div',{staticClass:"modal-body"},[(_vm.error.length !== 0)?_c('p',[_vm._v(_vm._s(_vm.$t('general.error_occurred'))+":")]):_vm._e(),_c('p',{domProps:{"innerHTML":_vm._s(_vm.$t(_vm.error))}}),_c('p',[_vm._v(_vm._s(_vm.$t('general.try_later')))])]),_c('div',{staticClass:"modal-footer"},[_c('footer',{staticClass:"actions",attrs:{"slot":"bottom"},slot:"bottom"},[_c('button',{staticClass:"btn primary",on:{"click":function($event){return _vm.reset()}}},[_vm._v(_vm._s(_vm.$t('general.close')))])])])]):_vm._e()],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "CreateWorkspace.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__